import {Injectable} from '@angular/core';
import {VisbosService} from '../../../core/visbos-core/graphql/visbos.service';
import {NgRedux, select} from '@angular-redux/store';
import {Observable} from 'rxjs';
import {Apollo, ApolloQueryObservable} from 'apollo-angular';
import {IAppState} from '../../../shared/redux-store';
import {SessionActions} from '../../../shared/actions';
import gql from 'graphql-tag';
import {
  AssignLocationToAssetsMutation,
  AssignLocationToAssetsMutationVariables,
  FetchAssetsQuery,
  FetchLocationsListQuery,
  FetchLocationsListQueryVariables,
  GetAssetQuery,
  UpdateAssetMutation
} from '../../../shared/graphql/graphql.types';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {ApolloQueryResult} from 'apollo-client';

export const assetFragments = {
  asset: gql`
    fragment AssetFields on Asset {
      cuid created_at updated_at deleted_at
      label serial_no, inventory_no stock_count
      article {cuid label}
      location {cuid label}
      identities {cuid ean is_seal}
      product_batch {cuid label use_before_date_formatted batch_number}
    }
  `,
  assetTree: gql`
    fragment AssetData on AssetTree {
      cuid created_at updated_at deleted_at
      name, label
      stock_count
      article {cuid label}
      location {cuid label}
      product_batch {cuid label use_before_date_formatted batch_number}
      identities {cuid ean is_seal}
      seals {cuid ean is_seal}
    }
  `,
  assetTreeNesting: null
};

assetFragments.assetTreeNesting = gql`
  fragment TreeData on AssetTree {
    ...AssetData
    children {
      ...AssetData
      children {
        ...AssetData
        children {
          ...AssetData
          children {
            ...AssetData
            children {
              ...AssetData
              children {
                ...AssetData
                children {
                  ...AssetData
                  children {
                    ...AssetData
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${assetFragments.assetTree}
`;

export const getAssetQuery = gql`
  query GetAsset($cuid: String!) {
    asset(cuid: $cuid) {
      ...TreeData
    }
  }
  ${assetFragments.assetTreeNesting}
`;

export const fetchAssetsQuery = gql`
  query FetchAssets {
    assetList {
      ...AssetFields
    }
  }
  ${assetFragments.asset}
`;

export const updateAssetMutation = gql`
  mutation UpdateAsset($cuid: String!, $assetData: AssetInput!) {
    updateAsset(cuid: $cuid, assetData: $assetData) {
      ...AssetFields
    }
  }
  ${assetFragments.asset}
`;

export const fetchLocationsList = gql`
  query FetchLocationsList($adminUnitCuid: String, $query: String) {
    locations(adminUnitCuid: $adminUnitCuid, query: $query) {
      cuid label
    }
  }
`;

export const assignLocationToAssets = gql`
  mutation AssignLocationToAssets($locationCuid: ID!, $locationType: String!, $assetCuids: [ID]) {
    assignAssetsToLocation(locationCuid: $locationCuid, locationType: $locationType, assetCuids: $assetCuids) {
      ...AssetFields
    }
  }
  ${assetFragments.asset}
`;

@Injectable()
export class AssetService extends VisbosService {

  @select(['session', 'activeAdminUnit'])
  activeAdminUnit$: Observable<string>;

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions, private http: HttpClient) {
    super(apollo, ngRedux, sessionActions);
  }

  public getAsset(cuid: string): ApolloQueryObservable<GetAssetQuery> {
    return this.fetchItems({
      query: getAssetQuery,
      variables: {
        cuid: cuid
      }
    });
  }

  public fetchAssets(): ApolloQueryObservable<FetchAssetsQuery> {
    return this.fetchItems({
      query: fetchAssetsQuery
    });
  }

  public updateAsset(cuid: string, assetData): Observable<ApolloQueryResult<UpdateAssetMutation>> {
    return this.mutate({
      mutation: updateAssetMutation,
      variables: {
        cuid: cuid,
        assetData: assetData
      }
    })
  }

  public exportAssetList() {
    const headers = new HttpHeaders();
    headers.set('Accept', 'text/plain');
    return this.http.get(`${environment.baseUrl}/api/export/gegenstaende`, {
      headers: headers,
      responseType: "arraybuffer",
      observe: 'response'
    });
  }

  public fetchLocationsOnce(variables?: FetchLocationsListQueryVariables): Observable<ApolloQueryResult<FetchLocationsListQuery>> {
    return this.fetchItemsOnce({
      query: fetchLocationsList,
      variables: variables
    });
  }

  public assignLocationToAssets(variables: AssignLocationToAssetsMutationVariables): Observable<ApolloQueryResult<AssignLocationToAssetsMutation>> {
    return this.mutate({
      mutation: assignLocationToAssets,
      variables: variables
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/asset-management/services/asset.service.ts